/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import {
  HlpDetailsDto,
  HlpRestService,
  ListingStatus,
  MasterZoneRestService,
  OfficeRestService,
  UserRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, InitViewService } from '@gh/common-shared';
import { GridLayoutService } from '@gh/core-ui-grid';
import { BuilderUserContext, FilterNames, FilterRestService } from '@gh/builder-shared';
import { Observable } from 'rxjs';
import { MasterZone } from '../../builder-shared/switch-to/resources/master-zone.model';
import { ID } from '@gh/common-shared-data';
import { Office } from '../../builder-shared/switch-to/resources/office.model';
import { FilteredDataQuery, getSlicedData, SlicedDataSource } from '@gh/core-data';
import { HlpListItem } from './hlp.model';
import { map$, switchMap$ } from '@gh/rx/operators';
import { combineLatest$, of$ } from '@gh/rx';
import { sortByStringField } from '@gh/core-util';

export type UserDetailsShort = UserDetailsShortDto;
export type UserDetailsShortDto = {
  userId: ID;
  userName: string;
};

@Injectable()
export class HlpListBusinessService implements InitViewService {

  constructor(private userContext: BuilderUserContext,
              private masterZoneRest: MasterZoneRestService,
              private officeRest: OfficeRestService,
              private hlpRest: HlpRestService,
              private userRest: UserRestService,
              private dictionaries: DictionaryService,
              private filterService: FilterRestService,
              private gridLayoutService: GridLayoutService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const masterZonesSub = this.userContext.corporateView ? this.findAllMasterZones() : of$([this.userContext.masterZone]);
    const gridLayoutSub =  this.gridLayoutService.get(this.userContext.corporateView ? 'hlpListCorp' : 'hlpListMaster');
    return this.filterService.get(this.userContext.corporateView ? FilterNames.hlpListCorp : FilterNames.hlpListMaster)
      .pipe(switchMap$((filter) => {
        let masterZoneId = '';
        if (this.userContext.masterView && this.userContext.masterZone) {
          masterZoneId = this.userContext.masterZone.id;
        }
        if (filter.masterZoneId) {
          masterZoneId = filter.masterZoneId;
        }
        return combineLatest$(
          [
            masterZonesSub,
            gridLayoutSub,
            masterZoneId ? this.findOfficesByMasterName(masterZoneId) : of$([]),
            filter.salesPersonId ? this.getSalesPerson(filter.salesPersonId) : of$({name: 'All', id: ''}),
          ]).pipe(map$(([masterZones, gridLayout, offices, salesPerson]) => ({
                             masterZones,
                             gridLayout,
                             offices,
                             salesPerson,
                             filter,
                           }
                         )));
      }));
  }

  findAllMasterZones(): Observable<MasterZone[]> {
    return this.masterZoneRest.findAll();
  }

  findAllAsDataSource(filter: any): SlicedDataSource<HlpListItem> {
    return this.hlpRest.findAllAsDataSource(filter);
  }

  findSalesPersonsByName(query: FilteredDataQuery<string>, filter: any): Observable<UserDetailsShort[]> {
    return this.userRest.findSalespersonsAsDataSource({
      ...filter,
      searchString: query.filter,
    }).select(query).pipe(map$(getSlicedData));
  }

  findOfficesByMasterName(masterId: ID, name?: string): Observable<Office[]> {
    return this.officeRest.findAllByMasterZone(masterId, ['deactivated', 'active'])
      .pipe(map$((res) => name ? sortByStringField(res.filter((office) => office.name.toLowerCase()
            .includes(name.toLowerCase())),        'status')
          : [{id: '', name: 'All'} as Office, ...sortByStringField(res, 'status')]));
  }

  delete(id: ID, listingStatus?: ListingStatus): Observable<any> {
    return this.hlpRest.delete(id, listingStatus);
  }

  get(id: ID): Observable<HlpDetailsDto> {
    return this.hlpRest.get(id);
  }

  getHlpInfo(id: ID): Observable<any> {
    return this.hlpRest.getHlpInfo(id);
  }

  getSalesPerson(id: ID): Observable<any> {
    return this.userRest.get(id).pipe(map$((user) => ({
      id: user.userId,
      name: user.contact?.name,
    })));
  }
}
